import * as React from "react"
import * as Yup from "yup"
import { CmsVendor } from "@modules/graphql/types"
import { useDatoCmsProjectsForIntegrationQuery } from "@modules/cms/shared/queries.generated"
import { RadioButtonConnectedField } from "gatsby-interface"
import Form from "@modules/form/components/Form"
import { composeIntegrationConfig, getInitialDataSource } from "../utils"
import { ConnectCmsFormProps } from "../types"
import { ConnectCmsFormControls } from "./ConnectCmsFormControls"
import { useSetCmsIntegration } from "../hooks/useSetCmsIntegration"
import { CmsDataType } from "@modules/cms/shared/utils"
import Loading from "@modules/ui/components/Loading"
import { ConnectCmsFormLayout } from "./ConnectCmsFormLayout"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { cmsIntegration as cmsIntegrationText } from "@modules/locales/default.js"
import { commonMessageTags, FormattedMessage } from "@modules/locales"

type FormValues = {
  dataSource: string
}

const validationSchema = Yup.object().shape<FormValues>({
  dataSource: Yup.string().required(
    cmsIntegrationText.messages.validationDatoCmsProjectRequired
  ),
})

export type ConnectDatoCmsFormProps = ConnectCmsFormProps

export function ConnectDatoCmsForm({
  siteId,
  siteName,
  onFetchError,
  onComplete,
  onCancel,
}: ConnectDatoCmsFormProps) {
  const { data, loading, error } = useDatoCmsProjectsForIntegrationQuery({
    fetchPolicy: `network-only`,
    variables: {
      siteId,
    },
    onError: onFetchError,
  })
  const [setCmsIntegration] = useSetCmsIntegration({
    siteId,
  })

  if (error) {
    return <ErrorAlert>{error.message}</ErrorAlert>
  }

  if (loading && data === undefined) {
    return (
      <Loading
        delay={1000}
        message={cmsIntegrationText.messages.loadingDatoCmsProjects}
      />
    )
  }

  const projects = data?.datoCMSProjectsForIntegration ?? []
  const options = projects.map(({ id, name }) => ({
    value: id,
    label: name,
  }))

  return (
    <Form<FormValues>
      initialValues={{
        dataSource: getInitialDataSource(projects),
      }}
      validationSchema={validationSchema}
      onSubmit={values => {
        return setCmsIntegration({
          variables: {
            config: composeIntegrationConfig(
              CmsDataType.DatoCmsProject,
              projects,
              values.dataSource
            ),
            siteId,
            vendor: CmsVendor.Datocms,
          },
        }).finally(onComplete)
      }}
      enableReinitialize
    >
      {({ isSubmitting }) => {
        return (
          <ConnectCmsFormLayout
            description={
              <FormattedMessage<"siteName", "strong">
                message={cmsIntegrationText.messages.chooseDatoCmsDataSource}
                values={{ siteName }}
                tags={commonMessageTags}
              />
            }
            controls={
              <ConnectCmsFormControls
                isSubmitting={isSubmitting}
                onCancel={onCancel}
              />
            }
          >
            <RadioButtonConnectedField
              name="dataSource"
              label={cmsIntegrationText.labels.datoCmsProject}
              options={options}
              variant="framed"
            />
          </ConnectCmsFormLayout>
        )
      }}
    </Form>
  )
}
